import React from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/PageHeader';

export default function Cookies({ onBack }) {
  const sections = [
    {
      title: "1. ¿Qué son las cookies y el almacenamiento local?",
      content: "Las cookies son pequeños archivos de texto que los sitios web almacenan en tu dispositivo. Veritas Scan hace un uso mínimo de cookies propias y utiliza principalmente localStorage (almacenamiento local del navegador) para gestionar la sesión y las preferencias del usuario."
    },
    {
      title: "2. Almacenamiento que usa Veritas Scan",
      content: `Veritas Scan utiliza los siguientes mecanismos de almacenamiento en tu navegador:

TOKEN DE SESIÓN (localStorage — necesario)
• Clave: veritas_token
• Qué guarda: un token JWT para mantenerte autenticado sin introducir contraseña en cada visita. No contiene datos personales, solo un identificador de sesión cifrado.
• Cuándo se elimina: al cerrar sesión o al caducar el token.

DATOS DE USUARIO (localStorage — necesario)
• Clave: veritas_user
• Qué guarda: nombre, email y plan (free/pro/business) para mostrártelos sin hacer una petición al servidor en cada carga.
• Cuándo se elimina: al cerrar sesión.

HISTORIAL DE ANÁLISIS (localStorage — solo usuarios sin cuenta)
• Clave: veritas_history
• Qué guarda: los últimos análisis realizados sin cuenta (veredicto, tipo de archivo, nombre y confianza), únicamente en tu dispositivo.
• Cuándo se elimina: al pulsar "Limpiar historial" en la app, o al borrar los datos del sitio desde el navegador.
• Nota: los usuarios con cuenta tienen su historial en la nube (MongoDB Atlas), no en localStorage.

CONSENTIMIENTO DE COOKIES (localStorage — necesario)
• Clave: veritas_cookie_consent
• Qué guarda: tu decisión sobre cookies (aceptar todo / solo necesarias) para no mostrarte el banner repetidamente.
• Cuándo se elimina: al borrar los datos del sitio desde el navegador.

PRIMERA VISITA / ONBOARDING (localStorage — funcional)
• Clave: veritas_onboarding_seen
• Qué guarda: si ya has visto el modal de bienvenida, para no mostrarlo más de una vez.
• Cuándo se elimina: al borrar los datos del sitio desde el navegador.`
    },
    {
      title: "3. Cookies de terceros",
      content: `Veritas Scan puede cargar cookies de los siguientes terceros según tu uso:

GOOGLE ANALYTICS (GA4) — analíticas (opcional, requiere consentimiento)
• Se carga únicamente si aceptas las cookies analíticas en el banner.
• Recopila datos estadísticos anónimos: páginas visitadas, tiempo en el sitio, origen del tráfico.
• No se usa para publicidad ni perfilado personal.
• Para desactivarlo: complemento de inhabilitación en tools.google.com/dlpage/gaoptout.

GOOGLE OAUTH — autenticación (si usas "Iniciar sesión con Google")
• Google puede establecer cookies durante el proceso de autenticación OAuth.
• Gestionadas íntegramente por Google y sujetas a su política de privacidad (policies.google.com/privacy).

STRIPE — pagos (si accedes al proceso de pago)
• Stripe establece cookies para garantizar la seguridad de la transacción y prevenir fraude.
• Gestionadas íntegramente por Stripe y sujetas a su política (stripe.com/privacy).
• Veritas Scan no tiene acceso a estas cookies.

Veritas Scan NO carga:
• Cookies publicitarias o de retargeting
• Píxeles de seguimiento de redes sociales (Facebook Pixel, etc.)
• SDKs de analítica de terceros más allá de Google Analytics (opcional)`
    },
    {
      title: "4. Servicios de procesamiento de contenido",
      content: `Cuando subes un archivo para análisis, este pasa por los siguientes servicios externos (sin cookies, mediante API):

• Cloudinary: almacenamiento temporal de imágenes para su procesamiento. Las imágenes se eliminan automáticamente tras el análisis.
• Google Cloud Vision SafeSearch: moderación automática de imágenes para detectar contenido inapropiado antes del análisis de IA.
• Google Gemini (Gemini 2.5 Flash): modelo de IA que procesa el contenido. Los archivos se eliminan automáticamente tras el análisis.
• ip-api.com: geolocalización aproximada de tu IP para mostrar información de sesión en el panel de seguridad (solo para usuarios registrados). No se almacenan datos.

Estos servicios no establecen cookies en tu navegador; la comunicación se realiza exclusivamente entre los servidores de Veritas Scan y las APIs externas.`
    },
    {
      title: "5. ¿Cómo gestionar o eliminar tus datos locales?",
      content: `Tienes pleno control sobre el almacenamiento local:

DESDE LA APLICACIÓN
• Cerrar sesión: elimina el token JWT y los datos de usuario del localStorage.
• Limpiar historial (panel lateral): borra el historial de análisis local.
• Rechazar cookies en el banner: Google Analytics no se cargará.

DESDE EL NAVEGADOR
• Chrome: Configuración → Privacidad → Borrar datos de navegación → Cookies y datos de sitios.
• Firefox: Preferencias → Privacidad → Borrar datos.
• Safari: Preferencias → Privacidad → Gestionar datos de sitios web.
• Edge: Configuración → Privacidad → Borrar datos de exploración.

AL USAR MODO INCÓGNITO/PRIVADO
• Todo el almacenamiento local se elimina automáticamente al cerrar la ventana.

PARA DATOS EN LA NUBE (usuarios registrados)
• El historial en la nube puede eliminarse desde la aplicación.
• La cuenta y todos los datos asociados pueden eliminarse desde Perfil → Cuenta → Eliminar cuenta.`
    },
    {
      title: "6. Banner de consentimiento",
      content: `Al visitar Veritas Scan por primera vez aparece un banner de consentimiento de cookies que te permite:

• Aceptar todo: permite el uso de Google Analytics además de las cookies estrictamente necesarias.
• Solo necesarias: únicamente se usarán las cookies imprescindibles para el funcionamiento del servicio (sesión, historial local, consentimiento, onboarding).

Puedes cambiar tu preferencia en cualquier momento borrando la clave veritas_cookie_consent del localStorage de tu navegador, lo que hará que el banner vuelva a aparecer.`
    },
    {
      title: "7. Cumplimiento legal",
      content: `Esta política cumple con:

• Reglamento General de Protección de Datos (RGPD / GDPR) — UE 2016/679
• Directiva ePrivacy (2002/58/CE) y su revisión propuesta
• Ley 34/2002 de Servicios de la Sociedad de la Información (LSSI) — España
• Ley Orgánica 3/2018 de Protección de Datos Personales (LOPDGDD) — España

El almacenamiento local empleado para la sesión (veritas_token, veritas_user) es estrictamente necesario para el funcionamiento del servicio autenticado y no requiere consentimiento explícito según el considerando 25 de la Directiva ePrivacy.

Google Analytics se carga únicamente con consentimiento previo explícito.`
    },
    {
      title: "8. Cambios en la política de cookies",
      content: "Podemos actualizar esta política en cualquier momento para reflejar nuevas funcionalidades del servicio o cambios en la legislación. Los cambios entrarán en vigor desde su publicación en esta página. Si tienes cuenta registrada y los cambios son significativos, te notificaremos por correo electrónico."
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 relative">
      <div className="scanlines" />
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(59, 130, 246, 0.08), transparent 60%)' }} />

      <PageHeader onBack={onBack} />

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 sm:space-y-8">

          <div className="space-y-2">
            <p className="font-mono text-xs tracking-widest text-blue-500 uppercase">Legal</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black">Política de Cookies</h1>
            <p className="text-zinc-500 font-mono text-sm">Última actualización: marzo 2026</p>
          </div>

          <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-xl p-4 text-emerald-300 font-mono text-xs leading-relaxed">
            Resumen: usamos localStorage para sesión, historial local y preferencias. Google Analytics es opcional y requiere tu consentimiento. Ninguna cookie publicitaria ni de seguimiento.
          </div>

          {sections.map(({ title, content }) => (
            <div key={title} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 sm:p-6 space-y-3">
              <h2 className="font-heading text-base sm:text-lg font-bold text-white">{title}</h2>
              <p className="text-zinc-400 leading-relaxed whitespace-pre-line text-sm">{content}</p>
            </div>
          ))}

        </motion.div>
      </main>
    </div>
  );
}
